import type { ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';

/**
 * The mockup's two screen skeletons.
 *
 * Every reference frame is built from one of exactly two compositions, and
 * putting them here rather than re-deriving them per screen is what stops the
 * flows drifting apart the way the previous build's did.
 *
 *   1. ACTION screens (Top_Up_1/2/3/5, Withdraw_1, Withdraw_Crypto_1/2,
 *      Withdraw_to_Card_1/2, Transfer_1/2, Profile_1/2): a full-bleed brand
 *      gradient panel with rounded bottom corners carrying the screen title and
 *      the inputs, then the page background below it carrying the primary
 *      action. `ScreenHero` + `ScreenBody`.
 *
 *   2. RESULT screens (Top_Up_6/7, Withdraw_Crypto_3/4, Withdraw_to_Card_3/4,
 *      Transfer_3/4): no gradient at all. A small centred title, a round glyph,
 *      the amount in display type, an optional status line, then grouped
 *      label/value cards and a single action. `ResultScreen`.
 *
 * The distinction is not decorative. The gradient means "you are still deciding
 * something"; its absence means "this already happened". Screens that mix the
 * two read as unfinished.
 */

/** Horizontal gutter shared by every screen, matching the mockup's 20px inset. */
export const SCREEN_GUTTER = 'px-5';

export interface ScreenHeroProps {
  /** The screen title, in `--on-gradient` display ink. */
  title: ReactNode;
  /** Form fields, option lists — whatever the user is choosing between. */
  children?: ReactNode;
  /** Rendered opposite the title, e.g. the avatar link on the main screen. */
  action?: ReactNode;
  /** Drop the title row entirely (the main screen draws its own header). */
  bare?: boolean;
  className?: string;
}

/**
 * The gradient panel.
 *
 * Full-bleed to the column edges and rounded only at the bottom, so it reads as
 * an extension of the status bar rather than a floating card — which is how
 * every reference frame draws it.
 *
 * Nothing smaller than 14px semibold goes on this surface. The gradient's teal
 * stop is light enough that white body copy loses contrast at its right-hand
 * end (see tokens.css), so labels here are semibold and captions live below the
 * panel, on the page background, not on it.
 */
export function ScreenHero({ title, children, action, bare, className }: ScreenHeroProps) {
  return (
    <div
      className={cn(
        'brand-gradient rounded-b-2xl text-[var(--on-gradient)]',
        'pt-[calc(var(--safe-top)+28px)] pb-7',
        SCREEN_GUTTER,
        className
      )}
    >
      {bare !== true && (
        <div className="flex items-center justify-between gap-3 pb-5">
          <h1 className="text-title font-semibold tracking-tight">{title}</h1>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

/** Everything under the gradient panel: the primary action, notes, history. */
export function ScreenBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex flex-col gap-3 pt-6', SCREEN_GUTTER, className)}>{children}</div>;
}

/** A field label sitting on the gradient. Semibold white, per the mockup. */
export function HeroLabel({
  htmlFor,
  children,
  className
}: {
  htmlFor?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label htmlFor={htmlFor} className={cn('text-body font-semibold', className)}>
      {children}
    </label>
  );
}

export interface ChoiceRowProps {
  /** Circular glyph on the left. */
  icon?: ReactNode;
  title: ReactNode;
  /** Second line under the title — a balance, a fee, a network hint. */
  subtitle?: ReactNode;
  /** Right-hand slot: a fee pill, a chevron, a value. */
  trailing?: ReactNode;
  /** Renders the mockup's selection control (filled tick / empty ring). */
  selected?: boolean;
  className?: string;
}

/**
 * The white row that every list in the mockup is made of: option lists on
 * Top_Up_1 and Withdraw_Crypto_1, method lists on Top_Up_5 and Withdraw_1,
 * settings rows on Profile_1/2.
 *
 * This renders the row's *contents* only. The interactive element wrapping it
 * differs by screen — a `role="radio"` for a choice, an anchor for navigation,
 * a `button` for an action — and baking one of those in here is how you end up
 * with a link that behaves like a radio.
 */
export function ChoiceRowContent({
  icon,
  title,
  subtitle,
  trailing,
  selected,
  className
}: ChoiceRowProps) {
  return (
    <span className={cn('flex w-full items-center gap-3.5', className)}>
      {icon !== undefined && (
        <span aria-hidden className="grid size-10 shrink-0 place-items-center">
          {icon}
        </span>
      )}

      {/*
        `flex-1 min-w-0` is load-bearing: without it the label column refuses to
        shrink and rides straight over the trailing fee pill on a narrow phone.
      */}
      <span className="flex min-w-0 flex-1 flex-col items-start text-left">
        {/*
          `break-words`, not `truncate`: a network name like
          "BNB Smart Chain (BEP-20)" has to stay readable in full on a 390px
          phone, and clipping the one string that tells the user which chain
          their money is going to is not an option.
        */}
        <span className="max-w-full break-words text-body font-semibold text-[var(--text-primary)]">
          {title}
        </span>
        {subtitle !== undefined && (
          <span className="max-w-full break-words text-body-sm text-[var(--text-muted)]">
            {subtitle}
          </span>
        )}
      </span>

      <span className="flex shrink-0 items-center gap-2">
        {trailing}
        {selected !== undefined && <SelectionMark selected={selected} />}
      </span>
    </span>
  );
}

/**
 * The mockup's selection control: a filled green disc with a white tick when
 * chosen, an empty brand-stroked ring when not.
 *
 * Green rather than brand blue because that is what the reference shows, and
 * because the tick is a white glyph on `--success` (3.4:1 for a 2px-stroked
 * graphical object, which clears the 3:1 non-text minimum). It is decorative
 * here regardless: the state it depicts is already carried by `aria-checked` on
 * the control that owns it.
 */
export function SelectionMark({ selected }: { selected: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        'grid size-7 shrink-0 place-items-center rounded-full border-2 transition-colors',
        selected
          ? 'border-[var(--success)] bg-[var(--success)] text-white'
          : 'border-[var(--brand)] bg-transparent'
      )}
    >
      {selected && (
        <svg viewBox="0 0 20 20" className="size-4" fill="none" aria-hidden>
          <path
            d="m5 10.5 3.4 3.4L15 7"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </span>
  );
}

/** Shared surface classes for a tappable white row on the gradient. */
export const CHOICE_ROW_SURFACE = cn(
  'flex w-full items-center rounded-md bg-[var(--surface)] px-4 py-3.5 text-left',
  'text-[var(--text-primary)] outline-none transition-colors duration-150',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white',
  'enabled:hover:bg-[var(--surface-hover)] disabled:cursor-not-allowed disabled:opacity-60'
);

/** Same row, sitting on the page background rather than the gradient. */
export const CHOICE_ROW_ON_PAGE = cn(
  'flex w-full items-center rounded-md bg-[var(--surface)] px-4 py-3.5 text-left',
  'text-[var(--text-primary)] outline-none transition-colors duration-150',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)]',
  'enabled:hover:bg-[var(--surface-hover)] disabled:cursor-not-allowed disabled:opacity-60'
);

export interface ResultScreenProps {
  /** Small centred heading — "Top Up", "Withdraw Crypto", "Transfer". */
  title: ReactNode;
  /** The round glyph above the amount. */
  icon: ReactNode;
  /** Display-size amount, already formatted and signed by the caller. */
  amount?: ReactNode;
  /**
   * `success` / `failed` draw the mockup's tick or cross; omit for a REVIEW
   * screen, which has no outcome yet.
   *
   * `pending` and `info` are not in the mockup and exist because the backend's
   * outcomes are not the mockup's. An outbound operation answers `Reserved`, and
   * `Reversed` is neither a success nor a failure — the money came back. Both
   * get their own mark rather than being rounded to the nearest of the two
   * states the reference happened to draw.
   */
  status?: 'success' | 'failed' | 'pending' | 'info';
  statusLabel?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * The confirmation and receipt skeleton (Top_Up_6/7, Withdraw_Crypto_3/4,
 * Transfer_3/4, Withdraw_to_Card_3/4).
 *
 * Flat page background, no gradient. The status line is a live region: on the
 * receipt screens it is the answer to the only question the user has, and it
 * frequently arrives after the screen has already painted.
 */
export function ResultScreen({
  title,
  icon,
  amount,
  status,
  statusLabel,
  children,
  className
}: ResultScreenProps) {
  return (
    <div className={cn('flex flex-col pt-[calc(var(--safe-top)+20px)]', SCREEN_GUTTER, className)}>
      <h1 className="text-center text-body font-semibold">{title}</h1>

      <span
        aria-hidden
        className="mt-8 grid size-16 place-items-center self-center rounded-full bg-[var(--surface)] text-[var(--brand)] shadow-[var(--shadow-sm)]"
      >
        {icon}
      </span>

      {amount !== undefined && (
        <p className="mt-4 text-center text-display font-semibold tracking-tight tabular-nums">
          {amount}
        </p>
      )}

      {status !== undefined && (
        <p
          role="status"
          data-testid="result-status"
          className="mt-3 flex items-center justify-center gap-2 text-body font-semibold"
        >
          <StatusMark status={status} />
          {statusLabel}
        </p>
      )}

      <div className="mt-10 flex flex-col gap-3">{children}</div>
    </div>
  );
}

function StatusMark({ status }: { status: 'success' | 'failed' | 'pending' | 'info' }) {
  const tone =
    status === 'success'
      ? 'bg-[var(--success)] text-white'
      : status === 'failed'
        ? 'bg-[var(--danger)] text-white'
        : status === 'info'
          ? 'bg-[var(--brand)] text-[var(--on-brand)]'
          : 'bg-[var(--warning)] text-white';

  return (
    <span aria-hidden className={cn('grid size-5 shrink-0 place-items-center rounded-full', tone)}>
      <svg viewBox="0 0 20 20" className="size-3.5" fill="none">
        {status === 'success' ? (
          <path
            d="m5 10.5 3.4 3.4L15 7"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : status === 'failed' ? (
          <path
            d="M6.5 6.5 13.5 13.5M13.5 6.5 6.5 13.5"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
          />
        ) : status === 'info' ? (
          // A U-turn: the money went out and came back. Neither a tick nor a
          // cross, because "Reversed" is neither.
          <path
            d="M6 13.5h5a3.5 3.5 0 0 0 0-7H6m0 0 2.5-2.5M6 6.5 8.5 9"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <path
            d="M10 5.5V10l3 2"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>
    </span>
  );
}

/**
 * A label above a value that must be read IN FULL — an address, a hash.
 *
 * `DetailGroup` truncates its values to keep a row on one line, which is right
 * for a fee and wrong for the 34 characters the user's money is being sent to.
 * The mockup draws the recipient as `TDBEKFsR…6NhA` on both the review and the
 * receipt; on the review that would remove the one thing the screen exists to
 * let the user check, so it wraps here instead.
 *
 * Monospaced with `break-all`: a proportional font makes `0` and `O`, `1` and
 * `l` interchangeable, which is exactly the confusion that loses funds.
 */
export function DetailBlock({
  label,
  value,
  action,
  className
}: {
  label: ReactNode;
  value: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-1.5 rounded-md bg-[var(--surface)] px-4 py-3.5 shadow-[var(--shadow-sm)]',
        'text-[var(--text-primary)]',
        className
      )}
    >
      <span className="text-body text-[var(--text-muted)]">{label}</span>
      <div className="flex items-start justify-between gap-3">
        <span className="min-w-0 flex-1 font-mono text-body-sm break-all">{value}</span>
        {/* `shrink-0`: without it the copy button compresses and the wrapping
            address runs underneath it. */}
        {action !== undefined && <span className="shrink-0">{action}</span>}
      </div>
    </div>
  );
}

export interface DetailRow {
  /** Stable key AND the test hook: rendered as `data-testid="detail-<id>"`. */
  id: string;
  label: ReactNode;
  value: ReactNode;
  /** Emphasised row — the mockup's "Total", in brand ink. */
  emphasis?: boolean;
  /** Trailing control, e.g. a copy button on a hash. */
  action?: ReactNode;
}

/**
 * A grouped white card of label/value rows — the mockup's receipt blocks.
 *
 * A description list, so a screen reader announces each label with its value.
 * Values are `tabular-nums` so a fee and a total do not jitter as a quote
 * refreshes.
 */
export function DetailGroup({
  rows,
  className,
  'aria-label': ariaLabel
}: {
  rows: readonly DetailRow[];
  className?: string;
  'aria-label'?: string;
}) {
  return (
    <dl
      aria-label={ariaLabel}
      className={cn(
        // Explicit ink for the same reason as SummaryList: a white surface must
        // never inherit the gradient panel's white text.
        'flex flex-col gap-2.5 rounded-md bg-[var(--surface)] px-4 py-3.5 shadow-[var(--shadow-sm)]',
        'text-[var(--text-primary)]',
        className
      )}
    >
      {rows.map((row) => (
        <div
          key={row.id}
          data-testid={`detail-${row.id}`}
          className="flex items-baseline justify-between gap-4"
        >
          <dt
            className={cn(
              'text-body',
              row.emphasis === true
                ? 'font-semibold text-[var(--text-primary)]'
                : 'text-[var(--text-muted)]'
            )}
          >
            {row.label}
          </dt>
          <dd
            className={cn(
              'flex min-w-0 items-center gap-1.5 text-right text-body font-medium tabular-nums',
              row.emphasis === true && 'font-semibold text-[var(--brand)]'
            )}
          >
            <span className="truncate">{row.value}</span>
            {row.action}
          </dd>
        </div>
      ))}
    </dl>
  );
}
